import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import '../../../assets/css/Variant/VariantNameList.css'; // your CSS import
const API_BASE_URL = process.env.REACT_APP_API_URL;
const VariantNameList = () => {
  const [variants, setVariants] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const navigate = useNavigate();

  // Load permissions from localStorage once on component mount
  const userPermissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const canRead = userPermissions.includes("read_VariantName");
  const canCreate = userPermissions.includes("create_VariantName");
  const canEdit = userPermissions.includes("update_VariantName");
  const canDelete = userPermissions.includes("delete_VariantName");
  const canToggleStatus = userPermissions.includes("update_VariantName");

  const fetchVariants = async () => {
    if (!canRead) return alert('You do not have permission to view variants.');

    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/variant-names`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVariants(res.data);
    } catch (error) {
      console.error('Fetch failed:', error);
      alert('Unauthorized. Please log in.');
    }
  };

  const toggleStatus = async (id) => {
    if (!canToggleStatus) return alert('You do not have permission to update status.');

    const token = localStorage.getItem('token');
    try {
      await axios.patch(`${API_BASE_URL}/api/variant-names/status/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchVariants();
    } catch (error) {
      console.error('Toggle status failed:', error);
      alert('Failed to update status.');
    }
  };

  const deleteVariant = async (id) => {
    if (!canDelete) return alert('You do not have permission to delete.');

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_BASE_URL}/api/variant-names/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchVariants();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete variant.');
    }
  };

  useEffect(() => {
    fetchVariants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter based on search input
  const filtered = variants.filter(v =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    v.code.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination calculation
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentVariants = filtered.slice(startIndex, startIndex + itemsPerPage);

  // Pagination handlers
  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Reset page to 1 on search change
  const onSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="container mt-4">
      <div className="header d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h4 className="variant-title mb-2 mb-md-0" style={{ color: '#102234' }}>
          Variant Name
        </h4>
        <button
          onClick={() => canCreate && navigate('/add-variant')}
          className="btn add-variant-btn"
          style={{
            backgroundColor: canCreate ? '#102234' : '#d3d3d3',
            color: canCreate ? 'white' : '#888888',
            cursor: canCreate ? 'pointer' : 'not-allowed',
            pointerEvents: canCreate ? 'auto' : 'none',
            border: 'none'
          }}
          aria-disabled={!canCreate}
          title={canCreate ? 'Add Variant Name' : 'Permission denied'}
        >
          <i className="bi bi-plus me-1"></i> Add Variant Name
        </button>
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Filter Result</label>
        <input
          type="text"
          className="form-control w-100 w-md-25"
          placeholder="🔍 Search by name or code"
          value={search}
          onChange={onSearchChange}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover text-center variant-table">
          <thead className="table-light">
            <tr>
              <th>No.</th>
              <th>Name</th>
              <th>Code</th>
              <th>Remarks</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentVariants.map((v, i) => (
              <tr key={v._id}>
                <td>{startIndex + i + 1}</td>
                <td>{v.name}</td>
                <td>{v.code}</td>
                <td>{v.remarks || '-'}</td>
                <td>
                  <div className="form-check form-switch d-flex justify-content-center">
                    <input
                      className="form-check-input status-switch"
                      type="checkbox"
                      checked={v.status}
                      onChange={() => canToggleStatus && toggleStatus(v._id)}
                      disabled={!canToggleStatus}
                      title={canToggleStatus ? "Toggle status" : "Permission denied"}
                      style={{ cursor: canToggleStatus ? "pointer" : "not-allowed" }}
                    />
                  </div>
                </td>
                <td>
                  <button
                    onClick={() => canEdit && navigate(`/edit-variant/${v._id}`)}
                    className="btn btn-link text-primary p-1"
                    aria-label="Edit"
                    disabled={!canEdit}
                    title={canEdit ? 'Edit variant' : 'Permission denied'}
                    style={{ cursor: canEdit ? 'pointer' : 'not-allowed', pointerEvents: canEdit ? 'auto' : 'none' }}
                  >
                    <i className="bi bi-pencil-square fs-5"></i>
                  </button>
                  <button
                    onClick={() => canDelete && deleteVariant(v._id)}
                    className="btn btn-link text-danger p-1"
                    aria-label="Delete"
                    disabled={!canDelete}
                    title={canDelete ? 'Delete variant' : 'Permission denied'}
                    style={{ cursor: canDelete ? 'pointer' : 'not-allowed', pointerEvents: canDelete ? 'auto' : 'none' }}
                  >
                    <i className="bi bi-trash-fill fs-5"></i>
                  </button>
                </td>
              </tr>
            ))}
            {currentVariants.length === 0 && (
              <tr>
                <td colSpan="6">No records found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Updated: Pagination Right Aligned */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-end align-items-center gap-3 mt-3">
          <Button
            variant="secondary"
            onClick={handlePrev}
            disabled={currentPage === 1}
          >
            <FaArrowLeft className="me-1" /> Previous
          </Button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <Button
            variant="secondary"
            onClick={handleNext}
            disabled={currentPage === totalPages}
          >
            Next <FaArrowRight className="ms-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default VariantNameList;
